import { useRef, useState, type ChangeEvent, type DragEvent, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useUploadAsset } from '../Content/Pages/Section/hooks/useUploadAsset';
import { ONBOARDING_PATHS } from './constants/paths';
import { OnboardingLayout } from './components/OnboardingLayout';
import { useWebBuilderAccess } from '../../hooks/useWebBuilderAccess';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';
import { useOnboarding } from './store/onboarding-store';
import { useBrandingDefaults } from './hooks/useBrandingDefaults';

function LogoPreview({
  logoUrl,
  isUploading,
  onChangeClick,
}: {
  logoUrl: string;
  isUploading: boolean;
  onChangeClick: () => void;
}) {
  return (
    <div className="vtwo:border vtwo:border-gray-200 vtwo:rounded-xl vtwo:py-10 vtwo:px-8 vtwo:flex vtwo:flex-col vtwo:items-center vtwo:gap-4">
      <img
        src={logoUrl}
        alt="Logo preview"
        className="vtwo:max-h-40 vtwo:max-w-full vtwo:object-contain"
      />
      <Button variant="outline" size="sm" disabled={isUploading} onClick={onChangeClick}>
        {isUploading ? <CircularProgress size={16} /> : 'Change logo'}
      </Button>
    </div>
  );
}

function LogoDropZone({
  isUploading,
  isDragging,
  onDrop,
  onDragOver,
  onDragLeave,
  onBrowseClick,
}: {
  isUploading: boolean;
  isDragging: boolean;
  onDrop: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: () => void;
  onBrowseClick: () => void;
}) {
  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={cn(
        'vtwo:border-2 vtwo:border-dashed vtwo:rounded-xl vtwo:py-16 vtwo:px-8 vtwo:flex vtwo:flex-col vtwo:items-center vtwo:gap-3 vtwo:transition-colors',
        isDragging ? 'vtwo:border-featured vtwo:bg-featured/5' : 'vtwo:border-gray-300'
      )}
    >
      {isUploading ? (
        <CircularProgress size={32} />
      ) : (
        <>
          <Upload className="vtwo:size-8 vtwo:text-gray-400" />
          <p className="vtwo:text-sm vtwo:font-medium">Drag and drop file here</p>
          <p className="vtwo:text-sm vtwo:text-gray-400">or</p>
          <Button variant="outline" size="sm" onClick={onBrowseClick}>
            Browse file
          </Button>
          <p className="vtwo:text-xs vtwo:text-gray-400 vtwo:mt-1">PNG or SVG, max 5 MB</p>
        </>
      )}
    </div>
  );
}

const LogoUploadStep = (): ReactElement => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const organisationId = searchParams.get('organisationId') ?? '';
  const { logoUrl, setLogoUrl } = useOnboarding();
  const { mutate: uploadAsset, isLoading: isUploading } = useUploadAsset();
  const { hasWebBuilderAccess } = useWebBuilderAccess();
  const { trackLogoUploaded } = useOnboardingTracker();
  // The organisation's app logo presets this step when one exists. Read the preset straight
  // from the hook rather than waiting for its store write, so the preview shows on the render
  // the request settles.
  const { isLoading: isBrandingLoading, presetLogoUrl } = useBrandingDefaults();
  const [isDragging, setIsDragging] = useState(false);

  const nextPath = hasWebBuilderAccess ? ONBOARDING_PATHS.homepage : ONBOARDING_PATHS.preview;

  const ACCEPTED_TYPES = new Set(['image/png', 'image/svg+xml']);
  const MAX_SIZE = 5 * 1024 * 1024;

  const handleFile = (file: File) => {
    if (!ACCEPTED_TYPES.has(file.type)) {
      enqueueSnackbar('Only PNG and SVG files are accepted', { variant: 'error' });
      return;
    }
    if (file.size > MAX_SIZE) {
      enqueueSnackbar('File must be under 5 MB', { variant: 'error' });
      return;
    }
    uploadAsset({ file, organisationId }, { onSuccess: (data) => setLogoUrl(data.url) });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) handleFile(file);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const logo = logoUrl || presetLogoUrl || '';
  const isAwaitingPreset = isBrandingLoading && !logo;

  let uploadArea: ReactElement;
  if (isAwaitingPreset) {
    uploadArea = (
      <div className="vtwo:border vtwo:border-gray-200 vtwo:rounded-xl vtwo:py-16 vtwo:flex vtwo:justify-center">
        <CircularProgress size={24} />
      </div>
    );
  } else if (logo) {
    uploadArea = (
      <LogoPreview logoUrl={logo} isUploading={isUploading} onChangeClick={openFilePicker} />
    );
  } else {
    uploadArea = (
      <LogoDropZone
        isUploading={isUploading}
        isDragging={isDragging}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onBrowseClick={openFilePicker}
      />
    );
  }

  return (
    <OnboardingLayout
      activeStep={2}
      backTo={ONBOARDING_PATHS.colour}
      primaryLabel="Continue"
      primaryDisabled={isAwaitingPreset}
      onPrimary={() => {
        if (!logo) {
          enqueueSnackbar('Please upload a logo before continuing', { variant: 'error' });
          return;
        }
        trackLogoUploaded();
        navigate({ pathname: nextPath, search: searchParams.toString() });
      }}
    >
      <div className="vtwo:text-center vtwo:mb-8">
        <h1 className="vtwo:text-3xl vtwo:font-bold vtwo:mb-3">Upload your logo</h1>
        <p className="vtwo:text-base vtwo:text-gray-600">
          Appears in the header and across your site
        </p>
      </div>

      <div className="vtwo:max-w-3xl vtwo:mx-auto">
        <div className="vtwo:bg-blue-50 vtwo:rounded-lg vtwo:px-6 vtwo:py-3 vtwo:text-center vtwo:text-sm vtwo:text-gray-700 vtwo:mb-6">
          A PNG or SVG file with a transparent background. Recommended size: at least 300px wide.
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/svg+xml"
          className="vtwo:hidden"
          onChange={handleFileChange}
        />

        {uploadArea}
      </div>
    </OnboardingLayout>
  );
};

export default LogoUploadStep;
